import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Lightweight markdown renderer for static legal copy.
 * Supports:
 *   ## / ### headings
 *   - / * bullets (single level)
 *   1. / 2. ordered list items
 *   **bold** inline
 *   blank line → paragraph break
 *   --- → horizontal rule
 *
 * Tables and nested lists aren't supported by design — kept tiny on purpose so
 * we don't pull in a markdown dependency for static copy that ships once.
 */

function inline(text) {
  // Render **bold** runs without dangerouslySetInnerHTML
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} className="text-white font-semibold">{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function parse(md) {
  // Pre-strip the title (# Single Title) — we render that separately in the header
  const lines = md.split('\n');
  const blocks = [];
  let currentList = null;          // { type: 'ul'|'ol', items: [] }

  const flushList = () => {
    if (currentList) { blocks.push(currentList); currentList = null; }
  };

  for (const raw of lines) {
    const line = raw.replace(/\r$/, '');
    const t = line.trim();

    if (!t) { flushList(); blocks.push({ type: 'space' }); continue; }
    if (t === '---') { flushList(); blocks.push({ type: 'hr' }); continue; }
    if (t.startsWith('### ')) { flushList(); blocks.push({ type: 'h3', text: t.slice(4) }); continue; }
    if (t.startsWith('## ')) { flushList(); blocks.push({ type: 'h2', text: t.slice(3) }); continue; }
    if (t.startsWith('# ')) { flushList(); /* title handled separately */ continue; }

    const ul = t.match(/^[-*]\s+(.*)$/);
    if (ul) {
      if (!currentList || currentList.type !== 'ul') { flushList(); currentList = { type: 'ul', items: [] }; }
      currentList.items.push(ul[1]);
      continue;
    }
    const ol = t.match(/^(\d+)\.\s+(.*)$/);
    if (ol) {
      if (!currentList || currentList.type !== 'ol') { flushList(); currentList = { type: 'ol', items: [] }; }
      currentList.items.push(ol[2]);
      continue;
    }
    // Skip basic table syntax — these docs have one tiny retention table; render its rows as paragraphs
    if (t.startsWith('|')) continue;

    flushList();
    blocks.push({ type: 'p', text: t });
  }
  flushList();
  return blocks;
}

function Renderer({ blocks }) {
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        if (b.type === 'h2') {
          // Anchor id (helps deep-linking from footer or in-page TOC later)
          const id = b.text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          return (
            <h2
              key={i} id={id}
              className="font-display font-bold text-xl md:text-2xl text-white mt-6 scroll-mt-20"
              data-testid={`legal-h2-${id}`}
            >
              {inline(b.text)}
            </h2>
          );
        }
        if (b.type === 'h3') {
          return (
            <h3 key={i} className="font-display font-semibold text-base md:text-lg text-white/90 mt-4">
              {inline(b.text)}
            </h3>
          );
        }
        if (b.type === 'p') {
          return (
            <p key={i} className="text-sm text-white/75 leading-relaxed">
              {inline(b.text)}
            </p>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul key={i} className="list-none space-y-1 pl-1">
              {b.items.map((it, j) => (
                <li key={j} className="text-sm text-white/80 flex items-start gap-2">
                  <span className="text-fuchsia-300 mt-1.5 leading-none">•</span>
                  <span>{inline(it)}</span>
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={i} className="space-y-1.5 pl-1">
              {b.items.map((it, j) => (
                <li key={j} className="text-sm text-white/80 flex items-start gap-2.5">
                  <span className="shrink-0 text-[11px] font-bold w-5 h-5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 flex items-center justify-center">{j + 1}</span>
                  <span className="pt-0.5">{inline(it)}</span>
                </li>
              ))}
            </ol>
          );
        }
        if (b.type === 'hr') return <hr key={i} className="border-white/8 my-4" />;
        if (b.type === 'space') return <div key={i} className="h-2" />;
        return null;
      })}
    </div>
  );
}

export default function LegalPage({ title, tagline, accent = '#A78BFA', content, backTo = '/app/help', testId = 'legal-page' }) {
  const blocks = useMemo(() => parse(content), [content]);

  return (
    <div className="max-w-3xl mx-auto pb-12" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <Link
          to={backTo}
          className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"
          data-testid="legal-back"
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl" style={{ color: accent }}>{title}</h1>
          {tagline && <p className="text-white/50 text-sm mt-0.5">{tagline}</p>}
        </div>
      </div>

      <div
        className="mt-5 rounded-3xl p-6 md:p-8 border border-white/10"
        style={{ background: `linear-gradient(135deg, ${accent}10, ${accent}05)` }}
      >
        <Renderer blocks={blocks} />
      </div>

      <div className="mt-8 text-center text-xs text-white/30">
        WorldVibe · <span className="text-white/50 italic">Meet the World in Real Time</span>
      </div>
    </div>
  );
}
